/**
 * Day 59 — Restock Intelligence page.
 *
 * Type 1 Standard layout (UI_STANDARDS §1, FUNCTIONAL_STANDARDS §1).
 * Reference implementation: src/pages/inventory/Inventory.tsx (close cousin —
 * same /inventory/* family, distributor-scoped flow, ExportButton + ArbiSelect
 * + AG Grid Infinite Row Model toolbar shape).
 *
 * Datasource: in-memory paginated from useRestockForecast (the
 * forecast_restock RPC returns the full set per call). AG Grid Infinite Row
 * Model is retained for cross-page consistency even though pagination is
 * client-side here.
 *
 * Velocity-blind rows (monthly_sales NULL or 0) appear in the grid when the
 * product has any inventory state, but their checkbox is disabled because
 * they have no suggested_reorder_qty to act on. Operators wanting to stage an
 * orphan-inventory SKU should do so from Buying Session, which has the full
 * manual-quantity UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { SELECTION_COLUMN_DEF } from '../../lib/gridUtils';
import { Search, Boxes } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useBuyingSession } from '../../hooks/useBuyingSession';
import { usePOStaging } from '../../hooks/usePOStaging';
import { useSessionTabs } from '../../hooks/useSessionTabs';
import { useRestockForecast, type ForecastBlobProduct } from '../../hooks/useRestockForecast';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ArbiToggle from '../../components/ui/ArbiToggle';
import ExportButton from '../../components/ui/ExportButton';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { ProvenanceTooltip } from '../../components/ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../../components/ui/ProvenanceIndicator';
import { useParamStore } from '../../hooks/useParamStore';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Module-level constants ──────────────────────────────────────────────────

// Fallback when fulfillment_path_config hasn't loaded yet (60s TTL cache miss) —
// the fpc-seed mirror (U10 A1 W1 re-point; Day 60b *_FALLBACK pattern). Picker
// derived from activePaths at render time. Picking a deactivated path would yield
// forecast_restock's `{ deactivated: true, products: [] }` sentinel response —
// wasted UX. Filtering at the picker eliminates the case.
const PATH_OPTIONS_FALLBACK = [
  { value: 'amazon_fba', label: 'Amazon FBA' },
  { value: 'amazon_fbm', label: 'Amazon FBM' },
  { value: 'ebay', label: 'eBay' },
  { value: 'walmart_self', label: 'Walmart Self' },
  { value: 'walmart_wfs', label: 'Walmart WFS' },
  { value: 'shopify', label: 'Shopify' },
];

// R1-F12: the comparison-window control + its comparison_days param were removed —
// the param was validated + echoed but never consumed in forecast_restock (a dead knob).
const FORECAST_OPTIONS = [
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
];

const URGENCY_CONFIG: Record<'red' | 'yellow' | 'green', { label: string; cls: string }> = {
  red: { label: 'Red', cls: 'bg-danger/15 text-danger' },
  yellow: { label: 'Yellow', cls: 'bg-warning/15 text-warning' },
  green: { label: 'Green', cls: 'bg-success/15 text-success' },
};

// Day 60.5 — `marketplaceToPath` converter deleted here and at BuyingSession.
// po_staging / po_line_items store the canonical long form (amazon_fba /
// amazon_fbm / ...). U10 W1 re-point: those keys ARE fpc.path_key values — the
// U5 fulfillment entities are the vocabulary of record; the stored
// public.marketplace enum merely mirrors them until it dies (slice-B family).

// Centralized cellStyle constants. Hoisted to module level so the columnDefs
// useMemo dep array doesn't need the styles in it (they're referentially
// stable across renders) — matches Inventory.tsx's column-def stability.
const STYLE_CENTER_FLEX: CellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const STYLE_START_FLEX: CellStyle = {
  display: 'flex',
  alignItems: 'center',
};
const STYLE_END_FLEX: CellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};
const STYLE_END_MONO: CellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  fontFamily: "'JetBrains Mono', monospace",
};

// ── Formatting helpers ──────────────────────────────────────────────────────

function fmtNumber(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return String(v);
}

function fmtDaysOfStock(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  // 0 is a finite valid value (stock just ran out at this moment, with active
  // velocity). Render as 0. Infinity case (run_rate=0 but stock>0) is filtered
  // upstream into NULL by the RPC, so it shows as — here.
  if (!Number.isFinite(v)) return '∞';
  return String(Math.floor(Number(v)));
}

function fmtMonthlySales(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '—';
  return String(v);
}

// ── Restock Page ────────────────────────────────────────────────────────────

export default function Restock(): JSX.Element {
  const { distributors } = useApp();
  const navigate = useNavigate();
  const restock = useRestockForecast();
  const { addItems } = usePOStaging();
  const { createSession } = useBuyingSession(null);
  const { openBuyingSessionTab } = useSessionTabs();
  const { activePaths } = useSettingsConfig();
  // Day 84.8 P2 — paramStore for staleThresholdDays resolution per Lock Q5
  // (manual_override_stale_days 90-day default; per-Restock divergence
  // deferred to Day 91a P7.5).
  const paramStore = useParamStore();

  const pathOptions = useMemo(() => {
    if (!activePaths || activePaths.length === 0) return PATH_OPTIONS_FALLBACK;
    return activePaths.map((p) => ({ value: p.path_key, label: p.display_name }));
  }, [activePaths]);

  const gridRef = useRef<AgGridReact<ForecastBlobProduct> | null>(null);
  const gridApiRef = useRef<GridApi<ForecastBlobProduct> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [hasVelocityFilter, setHasVelocityFilter] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [actionPending, setActionPending] = useState<boolean>(false);
  const [selectedCount, setSelectedCount] = useState<number>(0);

  const distOptions = useMemo(
    () => [
      { value: '', label: 'All distributors' },
      ...distributors.map((d) => ({ value: d.id, label: d.name })),
    ],
    [distributors]
  );

  // ── Derived: filtered + sorted product set ────────────────────────────────

  const filteredProducts = useMemo<ForecastBlobProduct[]>(() => {
    let rows = restock.data?.products ?? [];
    if (hasVelocityFilter) {
      rows = rows.filter((p) => p.monthly_sales !== null && (p.monthly_sales ?? 0) > 0);
    }
    const q = searchText.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (p) =>
          (p.isg_sku ?? '').toLowerCase().includes(q) || (p.title ?? '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [restock.data, hasVelocityFilter, searchText]);

  const totalCount = filteredProducts.length;
  const allRowsVelocityBlind =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => p.monthly_sales === null || (p.monthly_sales ?? 0) === 0);

  // ── Datasource: slice in-memory filtered set ──────────────────────────────

  const stateRef = useRef<{ filteredProducts: ForecastBlobProduct[] }>({
    filteredProducts,
  });
  stateRef.current = { filteredProducts };

  const getRowsFn = useCallback((p: IGetRowsParams): void => {
    const { startRow, endRow, successCallback } = p;
    const all = stateRef.current.filteredProducts;
    const slice = all.slice(startRow, endRow);
    successCallback(slice, all.length);
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<ForecastBlobProduct>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  // Refresh datasource when filtered set changes — AG Grid Infinite Row Model
  // doesn't auto-rerun getRows on prop change; explicit setGridOption needed.
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [filteredProducts, getRowsFn]);

  const onSelectionChanged = useCallback((): void => {
    if (gridApiRef.current) {
      setSelectedCount(gridApiRef.current.getSelectedRows().length);
    }
  }, []);

  // ── Cell renderers ────────────────────────────────────────────────────────

  const SkuCell = useCallback(
    (p: ICellRendererParams<ForecastBlobProduct>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      const isVelocityBlind = p.data.monthly_sales === null || (p.data.monthly_sales ?? 0) === 0;
      return (
        <div className="flex items-center gap-1.5 h-full">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (sku) navigate(`/catalog/products/${sku}`);
            }}
            className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
          >
            {sku}
          </button>
          {isVelocityBlind && (
            <span
              title="No velocity data — forecast unavailable. Stage manually from Buying Session."
              className="px-1 py-0.5 rounded bg-surface-2 text-2xs text-ink-muted whitespace-nowrap"
            >
              no velocity
            </span>
          )}
        </div>
      );
    },
    [navigate]
  );

  const LeadTimeCell = useCallback(
    (p: ICellRendererParams<ForecastBlobProduct>): JSX.Element | null => {
      if (!p.data) return null;
      const days = p.data.lead_time_days;
      const isDefault = p.data.lead_time_source === 'default';
      return (
        <span className="font-mono">
          {Math.floor(Number(days))}
          {isDefault && <span className="text-ink-muted"> (default)</span>}
        </span>
      );
    },
    []
  );

  const SuggestedQtyCell = useCallback(
    (p: ICellRendererParams<ForecastBlobProduct>): JSX.Element | null => {
      if (!p.data) return null;
      const qty = p.data.suggested_reorder_qty;
      if (qty === null || qty === undefined) return <span>—</span>;
      const isRed = p.data.urgency === 'red';
      return <span className={clsx('font-mono', isRed && 'font-semibold')}>{qty}</span>;
    },
    []
  );

  const UrgencyCell = useCallback(
    (p: ICellRendererParams<ForecastBlobProduct>): JSX.Element | null => {
      if (!p.data) return null;
      const u = p.data.urgency;
      if (u === null || u === undefined) {
        return <span className="text-ink-muted">—</span>;
      }
      const cfg = URGENCY_CONFIG[u];
      if (!cfg) return null;
      return (
        <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', cfg.cls)}>
          {cfg.label}
        </span>
      );
    },
    []
  );

  // Day 84.8 P2 — Monthly Sales cellRenderer with velocity_source ProvenanceTooltip
  // wrap (F15 first-priority per audit §16). Synthetic envelope per IA-7
  // (forecast_restock blob doesn't carry full provenance envelope; only the
  // engine attribution flag). staleThresholdDays = 90 per Lock 5 Q5 uniform
  // default; per-Restock divergence deferred to Day 91a P7.5 if surfaces.
  // velocity-blind rows (monthly_sales null/0) pass through to fmtMonthlySales
  // for em-dash placeholder; tooltip source attribution still rendered per
  // audit §16.6 "operator should distinguish which-tier-failed" intent.
  const MonthlySalesCell = useCallback(
    (p: ICellRendererParams<ForecastBlobProduct>): JSX.Element | null => {
      if (!p.data) return null;
      const value = p.value as number | null;
      const formatted = fmtMonthlySales(value);
      const velocitySource = p.data.velocity_source;
      const syntheticProv: ProvenanceMap | null = velocitySource
        ? { monthly_sales: { source: velocitySource } }
        : null;
      const staleDaysRaw = Number(paramStore.resolveKey('manual_override_stale_days')?.param.value);
      const staleThresholdDays = Number.isFinite(staleDaysRaw) ? staleDaysRaw : 90;
      return (
        <ProvenanceTooltip
          provenance={syntheticProv}
          fieldKey="monthly_sales"
          staleThresholdDays={staleThresholdDays}
          mode="cell"
        >
          <span className="font-mono">{formatted}</span>
        </ProvenanceTooltip>
      );
    },
    [paramStore]
  );

  // ── Column defs ───────────────────────────────────────────────────────────

  const columnDefs = useMemo<ColDef<ForecastBlobProduct>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        width: 160,
        pinned: 'left',
        suppressMovable: true,
        resizable: false,
        cellRenderer: SkuCell,
        cellStyle: STYLE_START_FLEX,
      },
      {
        headerName: 'Title',
        field: 'title',
        flex: 1,
        minWidth: 180,
        cellStyle: STYLE_START_FLEX,
      },
      {
        headerName: 'On Hand',
        field: 'on_hand',
        width: 90,
        valueFormatter: (p: ValueFormatterParams<ForecastBlobProduct>) =>
          fmtNumber(p.value as number | null),
        cellStyle: STYLE_END_MONO,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'On PO',
        field: 'on_purchase_order',
        width: 90,
        valueFormatter: (p: ValueFormatterParams<ForecastBlobProduct>) =>
          fmtNumber(p.value as number | null),
        cellStyle: STYLE_END_MONO,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Monthly Sales',
        field: 'monthly_sales',
        width: 110,
        cellRenderer: MonthlySalesCell,
        cellStyle: STYLE_END_FLEX,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Days of Stock',
        field: 'days_of_stock_remaining',
        width: 110,
        valueFormatter: (p: ValueFormatterParams<ForecastBlobProduct>) =>
          fmtDaysOfStock(p.value as number | null),
        cellStyle: STYLE_END_MONO,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Lead Time',
        field: 'lead_time_days',
        width: 120,
        cellRenderer: LeadTimeCell,
        cellStyle: STYLE_END_FLEX,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Suggested Qty',
        field: 'suggested_reorder_qty',
        width: 120,
        cellRenderer: SuggestedQtyCell,
        cellStyle: STYLE_END_FLEX,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Urgency',
        field: 'urgency',
        width: 90,
        cellRenderer: UrgencyCell,
        cellStyle: STYLE_CENTER_FLEX,
        headerClass: 'ag-center-aligned-header',
      },
    ],
    [SkuCell, MonthlySalesCell, LeadTimeCell, SuggestedQtyCell, UrgencyCell]
  );

  const defaultColDef = useMemo<ColDef<ForecastBlobProduct>>(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  // ── "Restock from suggestions" action ─────────────────────────────────────

  const distributorPicked = !!restock.params.distributorId;
  const actionDisabled = !distributorPicked || selectedCount === 0 || actionPending;
  const actionTooltip = !distributorPicked
    ? 'Pick a distributor first'
    : selectedCount === 0
      ? 'Select rows to restock'
      : '';

  const handleRestockFromSuggestions = useCallback(async (): Promise<void> => {
    const distributor = distributors.find((d) => d.id === restock.params.distributorId);
    if (!distributor) return;
    const selected = gridApiRef.current?.getSelectedRows() ?? [];
    const stageable = selected.filter((r) => r.suggested_reorder_qty != null);
    if (stageable.length === 0) {
      setToast({ kind: 'error', message: 'No suggestions to stage' });
      return;
    }

    setActionPending(true);

    // Fetch cost for the selected products (kept out of the RPC blob —
    // profitability concern, not forecasting concern). U10 A1 cost migration —
    // the junction embed (primary product_distributors row) is the canonical
    // read; products.cost_usd is the read-copy that DROPS at slice B. No-primary
    // → NULL (the read-copy's retain-last-known edge dies, the ruled semantic).
    const productIds = stageable.map((r) => r.product_id);
    const { data: costRows, error: costErr } = await supabase
      .from('products')
      .select('id, product_distributors ( cost_usd, is_primary )')
      .eq('product_distributors.is_primary', true)
      .in('id', productIds);
    if (costErr) {
      setToast({ kind: 'error', message: `Cost fetch failed — ${costErr.message}` });
      setActionPending(false);
      return;
    }
    const costMap = new Map(
      (costRows ?? []).map((r) => {
        const row = r as unknown as {
          id: string;
          product_distributors?: { cost_usd: number | null; is_primary: boolean }[] | null;
        };
        return [row.id, row.product_distributors?.[0]?.cost_usd ?? null] as const;
      })
    );

    // Create a fresh buying session for this distributor.
    let session;
    try {
      session = await createSession({ distributorId: distributor.id });
    } catch (e) {
      setToast({
        kind: 'error',
        message: `Session create failed — ${e instanceof Error ? e.message : String(e)}`,
      });
      setActionPending(false);
      return;
    }

    // Day 81 P5.6 (F53) — gate on activePaths + path-validity before any
    // write. Restock.params.selectedPath can itself be a now-deactivated
    // path if the user toggled it off in Settings between this page's
    // session loads; the F53 substrate catches the case here at the
    // layer closest to the staging write. activePaths.length === 0 covers
    // the all-paths-deactivated edge.
    const path = restock.params.selectedPath;
    if (activePaths.length === 0) {
      setToast({
        kind: 'error',
        message: 'No active fulfillment paths — enable one in Settings',
      });
      setActionPending(false);
      return;
    }
    if (!activePaths.some((p) => p.path_key === path)) {
      setToast({
        kind: 'error',
        message: 'Selected path is no longer active — pick a different one',
      });
      setActionPending(false);
      return;
    }
    // Bulk-stage with single .upsert([…]) round-trip via the new addItems helper.
    const toStage = stageable.map((r) => ({
      productId: r.product_id,
      isgSku: r.isg_sku,
      costPerUnit: Number(costMap.get(r.product_id) ?? 0),
      quantity: Math.max(1, r.suggested_reorder_qty ?? 1),
      distributorId: distributor.id,
      fulfillmentPath: path,
    }));
    const result = await addItems(toStage);

    // Open tab + navigate before showing toast so user lands in the new session.
    openBuyingSessionTab(session.id, `Buying Session — ${distributor.code}`);
    navigate(`/purchasing/buying-session?session=${session.id}`);

    setActionPending(false);

    if (result.failed > 0) {
      setToast({
        kind: 'error',
        message: `${result.inserted} staged, ${result.failed} failed`,
      });
    } else {
      setToast({
        kind: 'success',
        message: `Restocked ${result.inserted} SKU${result.inserted === 1 ? '' : 's'} into new session`,
      });
    }
  }, [
    distributors,
    restock.params,
    createSession,
    addItems,
    openBuyingSessionTab,
    navigate,
    activePaths,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        {/* Row 1: title + count + ExportButton + primary action */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Restock</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">
                {totalCount} {totalCount === 1 ? 'item' : 'items'}
              </span>
            )}
            {restock.loading && <span className="text-xs text-ink-muted">loading…</span>}
            {restock.error && <span className="text-xs text-danger">Error: {restock.error}</span>}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-restock" />
            <button
              onClick={handleRestockFromSuggestions}
              disabled={actionDisabled}
              title={actionTooltip}
              className="arbi-btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Restock from suggestions
              {selectedCount > 0 && distributorPicked ? ` (${selectedCount})` : ''}
            </button>
          </div>
        </div>

        {/* Row 2: path + forecast + growth + distributor + velocity toggle + search */}
        <div className="flex items-center gap-2 flex-wrap">
          <ArbiSelect
            value={restock.params.selectedPath}
            options={pathOptions}
            onChange={(v) =>
              restock.setParams({
                selectedPath: v as typeof restock.params.selectedPath,
              })
            }
            className="w-36"
            size="xs"
          />
          <ArbiSelect
            value={String(restock.params.forecastDays)}
            options={FORECAST_OPTIONS}
            onChange={(v) => restock.setParams({ forecastDays: Number(v) })}
            className="w-28"
            size="xs"
          />
          {/* Growth slider — inline custom range. Extract to ArbiRangeSlider.tsx
              on third use per the rule-of-three pattern-lift heuristic. */}
          <div className="flex items-center gap-2">
            <span className="text-2xs text-ink-muted">Growth</span>
            <input
              type="range"
              min={-0.5}
              max={0.5}
              step={0.05}
              value={restock.params.growthFactor}
              onChange={(e) => restock.setParams({ growthFactor: Number(e.target.value) })}
              aria-label="Growth factor"
              className="w-32 accent-accent cursor-pointer"
            />
            <span className="text-xs text-ink-secondary tabular-nums w-10 text-right font-mono">
              {restock.params.growthFactor >= 0 ? '+' : ''}
              {Math.round(restock.params.growthFactor * 100)}%
            </span>
          </div>
          <ArbiSelect
            value={restock.params.distributorId ?? ''}
            options={distOptions}
            onChange={(v) => restock.setParams({ distributorId: v || null })}
            className="w-40"
            size="xs"
          />
          <ArbiToggle
            checked={hasVelocityFilter}
            onChange={setHasVelocityFilter}
            label="Has velocity data"
            tooltip="Hide velocity-blind rows (no Algopix monthly_sales for this path)"
          />
          <span className="text-2xs text-ink-muted">Has velocity data</span>
          <div className="ml-auto relative w-60">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU or title..."
              aria-label="Search restock by SKU or title"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      {!distributorPicked ? (
        <div className="flex-1 flex flex-col items-center justify-center text-ink-muted">
          <Boxes size={48} className="mb-3" />
          <p className="text-sm">Select a distributor to forecast restock needs</p>
        </div>
      ) : (
        <>
          {allRowsVelocityBlind && !hasVelocityFilter && (
            <div className="flex-shrink-0 px-4 py-1.5 bg-warning/5 border-b border-warning/20 text-2xs text-warning">
              No products in this path have Algopix velocity data. Showing inventory-only rows.
              Forecast columns unavailable.
            </div>
          )}
          <div className="flex-1 p-2" style={{ minHeight: 0 }}>
            <AgGridReact
              ref={gridRef}
              theme={gridTheme}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowModelType="infinite"
              cacheBlockSize={200}
              maxBlocksInCache={50}
              selectionColumnDef={SELECTION_COLUMN_DEF}
              rowSelection={{
                mode: 'multiRow',
                enableClickSelection: false,
                checkboxes: true,
                headerCheckbox: false,
                isRowSelectable: (rowNode) => rowNode.data?.suggested_reorder_qty != null,
              }}
              onGridReady={onGridReady}
              onSelectionChanged={onSelectionChanged}
              animateRows={false}
              headerHeight={36}
              rowHeight={42}
              suppressCellFocus={true}
              suppressHorizontalScroll={true}
              noRowsOverlayComponent={() => (
                <div className="flex flex-col items-center justify-center h-full">
                  <Boxes size={48} className="text-ink-muted mb-3" />
                  <p className="text-sm text-ink-muted">
                    {hasVelocityFilter || searchText.trim()
                      ? 'No results match your filters'
                      : 'No products with velocity OR inventory state in this path'}
                  </p>
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}
